import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState, useRef, useCallback } from 'react';
import { createConsoleSink } from '@interlucent/logging';
import { useAppContextSafe } from '../contexts/AppContext';
import { ShareVersionDialog } from '../components/ShareVersionDialog';
import { logger } from '../log';
import '@interlucent/pixel-stream';
import styles from './preview.module.css';

const log = logger('preview');

// Get API base URL for HTTP calls (convert wss:// to https://)
function getApiBaseUrl(): string {
  const wsUrl = (import.meta as any).env.VITE_API_URL || 'wss://api.dev.interlucent.ai';
  return wsUrl.replace(/^wss:/, 'https:').replace(/^ws:/, 'http:');
}

// Get API endpoint (hostname only) for pixel-stream component
function getApiEndpoint(): string {
  const wsUrl = (import.meta as any).env.VITE_API_URL || 'wss://api.dev.interlucent.ai';
  try {
    const url = new URL(wsUrl);
    return url.hostname;
  } catch {
    return 'api.dev.interlucent.ai';
  }
}

// Connection states for the streaming preview
type ConnectionState =
  | 'idle'
  | 'resolving'
  | 'passcode_required'
  | 'connecting'
  | 'authenticating'
  | 'connected'
  | 'reconnecting'
  | 'error';

// Session storage key for preview context (used by share dialog)
const PREVIEW_CONTEXT_KEY = 'preview_context';

export interface PreviewContext {
  title?: string;
  projectId?: string;
  appId?: string;
  versionId?: string;
}

/**
 * Store preview context in sessionStorage before navigating to preview.
 * Called from console components that initiate preview.
 */
export function storePreviewContext(ctx: PreviewContext): void {
  try {
    sessionStorage.setItem(PREVIEW_CONTEXT_KEY, JSON.stringify(ctx));
  } catch {
    // sessionStorage might be unavailable
  }
}

/**
 * Read and clear preview context from sessionStorage.
 */
function consumePreviewContext(): PreviewContext | null {
  try {
    const stored = sessionStorage.getItem(PREVIEW_CONTEXT_KEY);
    if (stored) {
      sessionStorage.removeItem(PREVIEW_CONTEXT_KEY);
      return JSON.parse(stored);
    }
  } catch {
    // sessionStorage might be unavailable
  }
  return null;
}

// Declare pixel-stream element for TypeScript/JSX
declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      'pixel-stream': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement> & {
        'admission-token'?: string;
        'api-endpoint'?: string;
        'no-auto-connect'?: boolean;
        'mute'?: boolean;
        'reconnect-mode'?: 'none' | 'recover' | 'always';
        'reconnect-attempts'?: number | string;
        'reconnect-strategy'?: 'periodic' | 'exponential-backoff';
        'reconnect-interval'?: number | string;
        'controls'?: boolean;
        'swift-job-request'?: boolean;
        'preflight-deployment-check'?: boolean;
        'suppress-browser-keys'?: boolean;
        'queue-wait-tolerance'?: number | string;
        'webrtc-negotiation-tolerance'?: number | string;
        'debug'?: boolean;
        'log-level'?: string;
        'job-state'?: string;
        'session-state'?: string;
        'pointer-lock'?: boolean;
        'native-touch'?: boolean;
        'resolution-clamp'?: string;
        'force-relay'?: boolean;
        'use-mic'?: boolean;
        'resize-mode'?: string;
        'stream-quality'?: 'low-latency' | 'balanced' | 'quality';
      }, HTMLElement>;
    }
  }
}

export const Route = createFileRoute('/preview/$')({
  component: PreviewRoute,
});

function PreviewRoute() {
  const { _splat: splat } = Route.useParams();
  const navigate = useNavigate();
  const appContext = useAppContextSafe();
  const containerRef = useRef<HTMLDivElement>(null);
  const pixelStreamRef = useRef<HTMLElement | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [connectionState, setConnectionState] = useState<ConnectionState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [passcodeInput, setPasscodeInput] = useState("");
  const [shareId, setShareId] = useState<string | null>(null);
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [reconnectAttempt, setReconnectAttempt] = useState(0);
  const [micEnabled, setMicEnabled] = useState(false);
  const [isMuted, setIsMuted] = useState(true);

  // Context from sessionStorage (for share dialog)
  const [previewContext, setPreviewContext] = useState<PreviewContext | null>(null);

  // Validate shareId format (alphanumeric, 6+ chars)
  const isValidShareId = splat && /^[a-zA-Z0-9_-]{6,}$/.test(splat);

  // Token state - admission token is passed to pixel-stream which handles the rest
  const [admissionToken, setAdmissionToken] = useState<string | null>(null);

  // Stream configuration from share record (pointerLock, nativeTouch, resolutionClamp, etc.)
  const [streamConfig, setStreamConfig] = useState<Record<string, unknown> | null>(null);

  // Handle fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Handle escape key - need to use ref to avoid stale closure
  const handleCloseRef = useRef<() => void>(() => {});
  useEffect(() => {
    handleCloseRef.current = async () => {
      // End pixel-stream session if active
      const el = pixelStreamRef.current as any;
      if (el?.stop) {
        el.stop();
      }

      if (document.fullscreenElement) {
        await document.exitFullscreen();
      }

      if (previewContext) {
        navigate({ to: '/' });
      } else {
        window.location.href = 'https://interlucent.ai';
      }
    };
  });

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !document.fullscreenElement) {
        handleCloseRef.current();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Resolve share ID to admission token
  const resolveShare = async (id: string, passcode?: string, signal?: AbortSignal) => {
    setConnectionState('resolving');
    setError(null);

    try {
      const apiBaseUrl = getApiBaseUrl();
      const res = await fetch(`${apiBaseUrl}/preview/share/${id}/resolve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ passcode }),
        signal,
      });
      const data = await res.json();

      if (res.status === 401) {
        setConnectionState('passcode_required');
        return;
      }

      if (res.status === 410) {
        // Share link has expired
        throw new Error('This preview link has expired.');
      }

      if (!res.ok) {
        // Gateway returns { ok: false, message: "..." }
        throw new Error(data?.message || data?.error || 'Failed to resolve preview');
      }

      // Got admission token - pass it to pixel-stream which will handle everything
      setAdmissionToken(data.admissionToken);
      if (data.streamConfig) setStreamConfig(data.streamConfig);
      setConnectionState('connecting');
    } catch (err: any) {
      // Ignore abort errors (component unmounted)
      if (err?.name === 'AbortError') return;
      setError(err?.message || 'Failed to resolve preview');
      setConnectionState('error');
    }
  };

  // Determine entry point and resolve share
  useEffect(() => {
    // Consume preview context from sessionStorage (for share dialog)
    const ctx = consumePreviewContext();
    if (ctx) {
      setPreviewContext(ctx);
    }

    // All preview access goes through share resolution
    if (isValidShareId && splat) {
      setShareId(splat);
      setConnectionState('resolving');
      const controller = new AbortController();
      resolveShare(splat, undefined, controller.signal);
      return () => controller.abort();
    }

    // No valid entry point
    setError('Invalid or missing share ID');
    setConnectionState('error');
  }, [isValidShareId, splat]);

  // Handle pixel-stream session events
  const handleSessionStateChange = useCallback((e: CustomEvent) => {
    const { newState } = e.detail;
    log.info(`pixel-stream session state: ${newState}`);

    switch (newState) {
      case 'connecting':
        setConnectionState('connecting');
        break;
      case 'authenticating':
        setConnectionState('authenticating');
        break;
      case 'connected':
        setConnectionState('connected');
        setReconnectAttempt(0);
        break;
      case 'reconnecting':
        setConnectionState('reconnecting');
        break;
      case 'error':
        // Session errors are handled by the Bauhaus overlay (error state).
        // React connectionState stays unchanged so pixel-stream isn't unmounted.
        break;
      case 'idle':
        // Session ended cleanly — overlay shows PLAY button via CSS.
        break;
    }
  }, []);

  const handleSessionError = useCallback((e: CustomEvent) => {
    log.error('pixel-stream session error', e.detail);
    // Error display is handled by the Bauhaus overlay via session-state="error".
  }, []);

  const handleSessionReconnecting = useCallback((e: CustomEvent) => {
    const { attempt, maxAttempts } = e.detail;
    log.info(`Reconnecting (${attempt}/${maxAttempts})`);
    setReconnectAttempt(attempt);
  }, []);

  const handleSessionDisconnected = useCallback((e: CustomEvent) => {
    log.info('Session disconnected', e.detail);
    // Don't set connectionState to 'error' — that unmounts pixel-stream.
    // The Bauhaus overlay handles error display via session-state="error".
  }, []);

  // Attach event listeners to pixel-stream element.
  // Session auto-starts when the admission-token attribute is set (default auto-connect).
  useEffect(() => {
    const el = pixelStreamRef.current;
    if (!el) return;

    const sink = createConsoleSink();

    el.addEventListener('session-state-change', handleSessionStateChange as EventListener);
    el.addEventListener('session-error', handleSessionError as EventListener);
    el.addEventListener('session-reconnecting', handleSessionReconnecting as EventListener);
    el.addEventListener('session-disconnected', handleSessionDisconnected as EventListener);
    el.addEventListener('interlucent:log', sink as EventListener);

    const handleMicEnabled = () => setMicEnabled(true);
    const handleMicDisabled = () => setMicEnabled(false);
    const handleMicDenied = () => setMicEnabled(false);
    const handleMicMutedChange = (e: Event) => {
      const muted = (e as CustomEvent).detail;
      setMicEnabled(!muted);
    };
    el.addEventListener('microphone-enabled', handleMicEnabled);
    el.addEventListener('microphone-disabled', handleMicDisabled);
    el.addEventListener('microphone-permission-denied', handleMicDenied);
    el.addEventListener('microphone-muted-change', handleMicMutedChange);

    const handleMuteChange = (e: Event) => {
      const { newValue } = (e as CustomEvent).detail;
      setIsMuted(!!newValue);
    };
    el.addEventListener('mute-change', handleMuteChange);

    return () => {
      el.removeEventListener('session-state-change', handleSessionStateChange as EventListener);
      el.removeEventListener('session-error', handleSessionError as EventListener);
      el.removeEventListener('session-reconnecting', handleSessionReconnecting as EventListener);
      el.removeEventListener('session-disconnected', handleSessionDisconnected as EventListener);
      el.removeEventListener('interlucent:log', sink as EventListener);
      el.removeEventListener('microphone-enabled', handleMicEnabled);
      el.removeEventListener('microphone-disabled', handleMicDisabled);
      el.removeEventListener('microphone-permission-denied', handleMicDenied);
      el.removeEventListener('microphone-muted-change', handleMicMutedChange);
      el.removeEventListener('mute-change', handleMuteChange);
    };
  }, [admissionToken, handleSessionStateChange, handleSessionError, handleSessionReconnecting, handleSessionDisconnected]);


  const toggleFullscreen = async () => {
    if (!containerRef.current) return;

    try {
      if (!document.fullscreenElement) {
        await containerRef.current.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (err) {
      log.error('Fullscreen error', err);
    }
  };

  const toggleMic = () => {
    const el = pixelStreamRef.current as any;
    if (!el) return;
    el.setMicrophoneMuted(!el.isMicrophoneMuted);
  };

  const handleUnmute = () => {
    const el = pixelStreamRef.current as any;
    if (!el) return;
    el.mute = false;
  };

  const handleClose = () => {
    handleCloseRef.current();
  };

  const handleShare = () => {
    setIsShareDialogOpen(true);
  };

  const handleSubmitPasscode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!shareId || !passcodeInput) return;
    resolveShare(shareId, passcodeInput);
  };

  // Get status message based on connection state
  const getStatusMessage = (): string => {
    switch (connectionState) {
      case 'resolving':
        return 'Resolving share link...';
      case 'connecting':
        return 'Connecting...';
      case 'authenticating':
        return 'Authenticating...';
      case 'reconnecting':
        return `Reconnecting... (attempt ${reconnectAttempt})`;
      case 'connected':
        return 'Connected';
      default:
        return '';
    }
  };

  const handleStop = () => {
    const el = pixelStreamRef.current as any;
    if (el?.stop) el.stop();
  };

  return (
    <div ref={containerRef} className={styles.container}>
      {connectionState === 'passcode_required' && (
        <div className={styles.error}>
          <h2>Passcode required</h2>
          <form onSubmit={handleSubmitPasscode} className={styles.passcodeForm}>
            <input
              type="password"
              value={passcodeInput}
              onChange={(e) => setPasscodeInput(e.target.value)}
              placeholder="Enter passcode"
              className={styles.passcodeInput}
            />
            <button type="submit" className={styles.backButton} disabled={!passcodeInput}>
              Unlock
            </button>
          </form>
        </div>
      )}

      {connectionState === 'resolving' && (
        <div className={styles.loadingPlaceholder}>
          <p>{getStatusMessage()}</p>
        </div>
      )}

      {connectionState === 'error' && (
        <div className={styles.error}>
          <h2>Error</h2>
          <p>{error || 'An unknown error occurred'}</p>
          <button onClick={handleClose} className={styles.backButton}>
            Go Back
          </button>
        </div>
      )}

      {/* Show pixel-stream component once we have an admission token */}
      {admissionToken && connectionState !== 'error' && connectionState !== 'passcode_required' && (
        <div className={styles.streamingContainer}>
          <pixel-stream
            ref={pixelStreamRef}
            admission-token={admissionToken}
            api-endpoint={getApiEndpoint()}
            mute
            reconnect-mode="recover"
            queue-wait-tolerance={300}
            rendezvous-preference={120}
            webrtc-negotiation-tolerance={30}
            className={styles.pixelStream}
            swift-job-request
            log-level="debug"
            resize-mode="pureweb"
            stream-quality="quality"
            use-mic={streamConfig?.useMic ? true : undefined}
            pointer-lock={streamConfig?.pointerLock ? true : undefined}
            native-touch={streamConfig?.nativeTouch ? true : undefined}
            resolution-clamp={streamConfig?.resolutionClamp ? String(streamConfig.resolutionClamp) : undefined}
            force-relay={streamConfig?.forceRelay ? true : undefined}
            suppress-browser-keys={streamConfig?.suppressBrowserKeys ? true : undefined}
          />
          {/* Unmute button — visible whenever the stream is muted */}
          {isMuted && (
            <button
              onClick={handleUnmute}
              className={styles.unmuteButton}
              aria-label="Unmute audio"
              title="Unmute audio"
            >
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 5L6 9H2v6h4l5 4V5z" />
                <line x1="23" y1="9" x2="17" y2="15" />
                <line x1="17" y1="9" x2="23" y2="15" />
              </svg>
              <span>Unmute</span>
            </button>
          )}
          {/* Floating controls — visible while streaming */}
          <div className={`${styles.floatingControls} ${isMuted ? styles.floatingControlsShifted : ''}`}>
            <button onClick={handleStop} className={styles.floatingBtn} aria-label="Stop stream">
              <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="6" y1="6" x2="18" y2="18" /><line x1="18" y1="6" x2="6" y2="18" />
              </svg>
            </button>
            {previewContext?.projectId && appContext && (
              <button onClick={handleShare} className={styles.floatingBtn} aria-label="Create share link" title="Share">
                <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                </svg>
              </button>
            )}
            <button onClick={toggleFullscreen} className={styles.floatingBtn} aria-label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'} title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
              <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2">
                {isFullscreen ? (
                  <path d="M8 3v3a2 2 0 01-2 2H3m18 0h-3a2 2 0 01-2-2V3m0 18v-3a2 2 0 012-2h3M3 16h3a2 2 0 012 2v3" />
                ) : (
                  <path d="M8 3H5a2 2 0 00-2 2v3m18 0V5a2 2 0 00-2-2h-3m0 18h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3" />
                )}
              </svg>
            </button>
            <button onClick={handleClose} className={styles.floatingBtn} aria-label="Close preview" title="Close">
              <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4m7 14l5-5-5-5m5 5H9" />
              </svg>
            </button>
          </div>
          {/* Mic toggle — bottom-right, visible when use-mic is configured */}
          {streamConfig?.useMic && (
            <button
              onClick={toggleMic}
              className={`${styles.micToggle} ${micEnabled ? styles.micActive : ''}`}
              aria-label={micEnabled ? 'Disable microphone' : 'Enable microphone'}
              title={micEnabled ? 'Disable microphone' : 'Enable microphone'}
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
                <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
              </svg>
              {!micEnabled && (
                <svg className={styles.micSlash} viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="3" y1="3" x2="21" y2="21" />
                </svg>
              )}
            </button>
          )}
        </div>
      )}

      {/* Share dialog - only available for authenticated users with project context */}
      {previewContext?.projectId && previewContext?.appId && appContext && (
        <ShareVersionDialog
          isOpen={isShareDialogOpen}
          versionId={previewContext.versionId || ''}
          versionName={previewContext.versionId ? `Version ${previewContext.versionId.slice(-8)}` : 'Current Preview'}
          appId={previewContext.appId}
          projectId={previewContext.projectId}
          onClose={() => setIsShareDialogOpen(false)}
        />
      )}
    </div>
  );
}
